import React, { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress } from "@mui/material";
import { store } from "../../config/store/store";
import AuthContext from "../../context/AuthContext";
import SelectField from "./SelectField";
import { getCurrencies } from "./CurrencySelectField.thunk";
import { useEffectOnce } from "../../utils/effects";
import {
  CurrencySelectFieldContainer,
  LoadingIndicator,
} from "./CurrencySelectField.styles";

/**
 * CurrencySelectField - Wrapper component that encapsulates SelectField with currency logic
 * 
 * This component:
 * - Evaluates if currencies are loaded in the store
 * - If not loaded, automatically loads them from the server
 * - Passes currencies to the underlying SelectField
 * 
 * Maintains project standard: thunk and slice within the same module
 * 
 * @param {Object} props - Props passed directly to SelectField
 * @param {string} props.name - Field name (required)
 * @param {string} props.value - Current field value
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.onBlur - Blur handler
 * @param {string} props.label - Field label (default: "Currency")
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.error - Error message
 * @param {boolean} props.touched - Whether the field has been touched
 * @param {boolean} props.disabled - Whether the field is disabled
 * @param {number} props.maxLength - Maximum length (default: 3)
 * @param {Object} props.sx - Custom styles
 * @param {Object} props.selectProps - Additional props for Select
 * @param {Object} props.menuItemProps - Additional props for MenuItem
 * @param {...Object} props.otherProps - Any other props that SelectField accepts
 */
const CurrencySelectField = ({
  name,
  value,
  onChange,
  onBlur,
  label = "Currency",
  required = false,
  error = "",
  touched = false,
  disabled = false,
  maxLength = 3,
  sx = {},
  selectProps = {},
  menuItemProps = {},
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const { currencies, status } = useSelector((state) => state.currencySelectField);
  
  const isLoading = status === "loading" && currencies.length === 0;

  // Evaluates if currencies are loaded and loads them if necessary (only once)
  useEffectOnce(() => {
    if (!token) return;

    // Get current state from store to check if currencies are already loaded
    // This prevents multiple calls when multiple instances of the component mount
    const currentState = store.getState();
    const { currencies: storeCurrencies, status: storeStatus } =
      currentState.currencySelectField;

    // Only loads if:
    // 1. No currencies in store (empty array)
    // 2. Status is not 'loading' or 'succeeded' (to avoid multiple calls)
    if (
      storeCurrencies.length === 0 &&
      storeStatus !== "loading" &&
      storeStatus !== "succeeded"
    ) {
      dispatch(getCurrencies({ token }));
    }
  });

  // Passes all props to SelectField, but with currencies as options
  return (
    <CurrencySelectFieldContainer sx={sx}>
      {isLoading ? (
        <LoadingIndicator>
          <CircularProgress size={20} />
        </LoadingIndicator>
      ) : (
        <SelectField
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          label={label}
          required={required}
          error={error}
          touched={touched}
          disabled={disabled}
          maxLength={maxLength}
          options={currencies}
          sx={{}}
          selectProps={selectProps}
          menuItemProps={menuItemProps}
          {...otherProps}
        />
      )}
    </CurrencySelectFieldContainer>
  );
};

export default CurrencySelectField;

